/**
 * Client-safe helpers for "what the visitor actually typed". Every submit form sends its
 * raw field values alongside the cleaned-up payload, so the operator can always see the
 * original — a link we couldn't parse becomes a note to read, never a lost submission.
 */

export type RawInput = Record<string, string>;

/** Per-field cap. Long enough for a real note, short enough that the log can't be abused. */
export const RAW_FIELD_MAX = 2000;
const RAW_FIELDS_MAX = 30;

/** Keys that hold an email address — never written to the failure log. */
const EMAIL_KEY = /e-?mail/i;
/** Email-shaped text inside free-text values ("reach me at bob@x.com"). */
const EMAIL_IN_TEXT = /[^\s@<>()"',;:]+@[^\s@<>()"',;:]+\.[a-z]{2,}/gi;
/** Total budget for one row's raw input. */
const RAW_TOTAL_MAX = 8000;

/**
 * Normalize an untrusted raw-input object: string values only, bounded size, and
 * (optionally) email fields removed. Returns null when nothing is left.
 */
export function sanitizeRawInput(
  input: unknown,
  { dropEmail = false }: { dropEmail?: boolean } = {},
): RawInput | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const out: RawInput = {};
  let n = 0;
  let total = 0;
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (n >= RAW_FIELDS_MAX) break;
    if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean") continue;
    if (dropEmail && EMAIL_KEY.test(k)) continue;
    let s = String(v);
    if (s.trim() === "") continue;
    if (dropEmail) s = s.replace(EMAIL_IN_TEXT, "[email removed]");
    const budget = Math.min(RAW_FIELD_MAX, RAW_TOTAL_MAX - total);
    if (budget <= 0) break;
    const val = s.length > budget ? `${s.slice(0, budget)}…` : s;
    out[k.slice(0, 64)] = val;
    total += val.length;
    n++;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Labels on a stored submission describing input we accepted but couldn't use as-is.
 * They route the submission to a human (no AI spend on junk) and render as badges in the
 * review queue.
 */
export type InputIssue =
  | "source_url_unparseable"
  | "website_unparseable"
  | "no_source"
  | "evidence_maybe_irrelevant"
  | "evidence_unreadable";

export const INPUT_ISSUE_LABEL: Record<InputIssue, string> = {
  source_url_unparseable: "Source link couldn't be parsed — see what they typed",
  website_unparseable: "Website couldn't be parsed — see what they typed",
  no_source: "No link or photo — visitor's word only",
  evidence_maybe_irrelevant: "AI thinks the photo may not be venue evidence",
  evidence_unreadable: "They attached a file we couldn't read — ask them to resend?",
};
